import React, { useMemo } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import { formatDateUTC } from "..";
import { Location } from "../../../models/marine.traffic";
import { ShipmentType } from "../../../util/enums";
import { DefaultTooltip } from "../../Generic/Tooltip";
import { ProgressBarContainerEvent } from "../../VoyageDetailsComponent";
import { LinearProgress } from "./components/LinearProgress";
import { LocationLabel } from "./components/LocationLabel";
import {
  DateLabel,
  DateLabelSubtitle,
  HeaderContainer,
  InfoContainer,
  ShipContainer,
  ShipStatus,
  IconContainer,
  InfoIcon,
  HeaderImage,
  ShipmentPrediction,
  useStyles,
  TrackingInfos,
} from "./styles";

const AIRPLANE_IMAGE = require("../../../assets/img/airplane.png");
const SHIP_IMAGE = require("../../../assets/img/container-ship.png");

interface TrackingHeaderProps {
  steps: ProgressBarContainerEvent[];
  activeStep: number;
  locations: Location[] | undefined;
  ATA: string | undefined;
  plannedETA: string | undefined;
  predictedETA: string | undefined;
  atd: string | undefined;
  shipmentStatus: string | undefined;
  delayedDays: number | undefined;
  shipmentType: string | undefined;
  originPort?: string;
  finalPort?: string;
  etd?: string | undefined;
  eta?: string | undefined;
}

export const TrackingHeader = ({
  steps = [],
  activeStep,
  locations,
  ATA,
  plannedETA,
  predictedETA,
  atd,
  shipmentStatus,
  delayedDays,
  shipmentType,
  originPort,
  finalPort,
  eta,
  etd,
}: TrackingHeaderProps) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const progressPercentage = useMemo(() => {
    return steps ? (activeStep / steps.length) * 100 : 0;
  }, [steps, activeStep]);

  const firstStep = useMemo(() => steps[0], [steps]);
  const lastStep = useMemo(() => steps?.[Number(steps.length - 1) ?? 0], [steps]);
  const firstStepPort = useMemo(
    () => locations?.find((location) => location.portId === firstStep.portId),
    [firstStep, locations]
  );
  const lastStepPort = useMemo(
    () => locations?.find((location) => location.portId === lastStep.portId),
    [lastStep, locations]
  );

  const isCompleted = useMemo(() => progressPercentage === 100, [progressPercentage]);
  const isAirTracking = shipmentType === ShipmentType.AIR;
  const renderDelayCount = () => {
    if (!delayedDays) return null;

    return (
      <Grid item justifyContent="center" alignItems="center">
        <ShipStatus
          color="rgba(241, 246, 251, 1)"
          backgroundColor="rgba(240, 68, 56, 0.5)"
          id="delay"
          height="32px"
        >
          {`${delayedDays} ${translate(
            delayedDays === 1 ? "dayDelay" : "daysDelay"
          )?.toLowerCase()}`}
        </ShipStatus>
      </Grid>
    );
  };

  const renderATDByType = (jumpLine?: boolean) => {
    if (isAirTracking) {
      return (
        <>
          {etd && !atd && (
            <div className={classes.atdDesktop}>
              <DateLabel light>
                {translate("etd")}
                {jumpLine && ":"}
              </DateLabel>
              <DateLabel active>{formatDateUTC(etd) ?? "\u00A0"}</DateLabel>
            </div>
          )}
          {atd && (
            <div className={classes.atdDesktop}>
              <DateLabel light active>
                {translate("atd")}
                {jumpLine && ":"}
              </DateLabel>
              <DateLabel active>{formatDateUTC(atd) ?? "\u00A0"}</DateLabel>
            </div>
          )}
        </>
      );
    }

    return (
      <div className={classes.atdDesktop}>
        <DateLabel light active>
          {translate("atd")}
          {jumpLine && ":"}
        </DateLabel>
        <DateLabel active>{formatDateUTC(atd) ?? "\u00A0"}</DateLabel>
      </div>
    );
  };
  const renderPredictsByType = (jumpLine?: boolean) => {
    if (isAirTracking) {
      return (
        <>
          {eta && !ATA && (
            <div className={classes.atdDesktop}>
              <DateLabel light>
                {translate("eta")}
                {jumpLine && ":"}
              </DateLabel>
              <DateLabel>{formatDateUTC(eta) ?? "\u00A0"}</DateLabel>
            </div>
          )}
          {ATA && (
            <div className={classes.atdDesktop}>
              <DateLabel light active>
                {translate("ata")}
                {jumpLine && ":"}
              </DateLabel>
              <DateLabel active>{formatDateUTC(ATA) ?? "\u00A0"}</DateLabel>
            </div>
          )}
          {predictedETA && (
            <div className={classes.predictionsWrapper}>
              <DateLabelSubtitle>
                <DefaultTooltip
                  placement="right"
                  title={translate(
                    "thisRepresentsTheInitialEstimatedTimeOfArrivalProvidedByTheCarrier"
                  )}
                  className={classes.tooltip}
                >
                  <IconContainer>
                    <InfoIcon size={12} />
                  </IconContainer>
                </DefaultTooltip>
                {translate("plannedETA")}
                {jumpLine && ":"}
              </DateLabelSubtitle>
              <DateLabelSubtitle>{predictedETA ?? "\u00A0"}</DateLabelSubtitle>
            </div>
          )}
        </>
      );
    }

    return (
      <>
        {predictedETA && (
          <div className={classes.predictionsWrapper}>
            <DateLabel light active={isCompleted}>
              {translate(shipmentType === ShipmentType.AIR ? "eta" : "shipeeziPredictsETA")}
              {jumpLine && ":"}
            </DateLabel>
            <DateLabel active={isCompleted}>{formatDateUTC(predictedETA) ?? "\u00A0"}</DateLabel>
          </div>
        )}
        {!isCompleted && plannedETA && (
          <div className={classes.predictionsWrapper}>
            <DateLabelSubtitle>
              <DefaultTooltip
                placement="right"
                title={translate(
                  "thisRepresentsTheInitialEstimatedTimeOfArrivalProvidedByTheCarrier"
                )}
                className={classes.tooltip}
              >
                <IconContainer>
                  <InfoIcon size={12} />
                </IconContainer>
              </DefaultTooltip>
              {translate("plannedETA")}
              {jumpLine && ":"}
            </DateLabelSubtitle>
            <DateLabelSubtitle>{plannedETA ?? "\u00A0"}</DateLabelSubtitle>
          </div>
        )}
        {isCompleted && ATA && (
          <div className={classes.predictionsWrapper}>
            <DateLabel active>
              {translate("ata")}
              {jumpLine && ":"}
            </DateLabel>
            <DateLabel active>{ATA ?? "\u00A0"}</DateLabel>
          </div>
        )}
      </>
    );
  };

  const renderShipmentPredictions = (id: string, jumpLine?: boolean) => {
    return (
      <ShipmentPrediction id={id}>
        <div className={classes.shipmentActualDates}>
          {renderATDByType(jumpLine)}
          {shipmentType === ShipmentType.AIR && plannedETA ? (
            <div className={classes.predictionsWrapper}>
              <DateLabelSubtitle>
                <DefaultTooltip
                  placement="right"
                  title={translate(
                    "thisRepresentsTheInitialEstimatedTimeOfArrivalProvidedByTheCarrier"
                  )}
                  className={classes.tooltip}
                >
                  <IconContainer>
                    <InfoIcon size={12} />
                  </IconContainer>
                </DefaultTooltip>
                {translate("plannedETD")}
                {jumpLine && ":"}
              </DateLabelSubtitle>
              <DateLabelSubtitle>{plannedETA ?? "\u00A0"}</DateLabelSubtitle>
            </div>
          ) : null}
        </div>
        <div className={classes.shipmentPredictions}>{renderPredictsByType(jumpLine)}</div>
      </ShipmentPrediction>
    );
  };

  const renderHeaderImage = () => {
    switch (shipmentType) {
      case ShipmentType.AIR:
        return <HeaderImage src={AIRPLANE_IMAGE} alt="airplane" className={classes.imgPlane} />;
      case ShipmentType.SEA:
      default:
        return <HeaderImage src={SHIP_IMAGE} alt="ship-with-containers" />;
    }
  };

  return (
    <HeaderContainer>
      <ShipContainer>
        <div className={isAirTracking ? classes?.airImgContainer : classes.imgContainer}>
          {renderHeaderImage()}
        </div>
        <TrackingInfos height={isAirTracking ? "32px" : undefined}>
          <ShipStatus id="status" delayedDays={delayedDays}>
            {shipmentStatus}
          </ShipStatus>
          {renderDelayCount()}
        </TrackingInfos>
      </ShipContainer>
      <InfoContainer>
        <LinearProgress percentage={progressPercentage} id="mobileProgressionComponent" />
        <div className={classes.portsContainer}>
          <div className={classes.shipmentPredictions}>
            <LocationLabel
              location={firstStepPort}
              active
              isAirTracking={isAirTracking}
              portName={originPort?.toUpperCase()}
            />
            <div className={classes.atdMobile}>
              <DateLabel light active>
                {translate("actualTimeOfDeparture")}
              </DateLabel>
              <DateLabel active>{formatDateUTC(atd) ?? "\u00A0"}</DateLabel>
            </div>
          </div>
          <div>
            <LocationLabel
              location={lastStepPort}
              active={isCompleted}
              id="lastStepPort"
              isAirTracking={isAirTracking}
              portName={finalPort?.toUpperCase()}
            />
            {renderShipmentPredictions("mobilePredictionsComponent")}
          </div>
        </div>
        <LinearProgress percentage={progressPercentage} id="desktopProgressionComponent" />
        {renderShipmentPredictions("desktopPredictionsComponent", true)}
      </InfoContainer>
    </HeaderContainer>
  );
};
